import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, AlertTriangle, RefreshCw, User, CalendarDays, Paperclip, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button, Modal, DateInput, formatDateDisplay, EmptyState } from '../common';
import { SearchableSelect } from '../common/SearchableSelect';
import ConfirmDelete from './common/ConfirmDelete';
import StatusDot from './common/StatusDot';
import UserSelect from './common/UserSelect';
import ActivityDetailModal from './ActivityDetailModal';
import StatusMover from '../common/StatusMover';
import {
  getDossierActivities, createDossierActivity, updateDossierActivity, deleteDossierActivity,
} from '../../services/dossier.service';
import type { DossierActivity } from '../../types';

interface Props {
  dossierId: number;
  projectId: number;
  // Notifică pagina-părinte după un CRUD (badge-uri, sumar). Opțional.
  onRefresh?: () => void;
}

type ActivityStatus = DossierActivity['status'];

const STATUS_OPTIONS: { value: ActivityStatus; label: string; color: 'gray' | 'yellow' | 'green' }[] = [
  { value: 'pending', label: 'De făcut', color: 'gray' },
  { value: 'in_progress', label: 'În lucru', color: 'yellow' },
  { value: 'completed', label: 'Finalizat', color: 'green' },
];

const STATUS_DOT_COLOR: Record<string, 'gray' | 'yellow' | 'green'> = {
  pending: 'gray', in_progress: 'yellow', completed: 'green',
};

const EMPTY_FORM = {
  name: '',
  description: '',
  status: 'pending' as ActivityStatus,
  responsible_user_id: null as number | null,
  start_date: '',
  due_date: '',
};

const toDateStr = (v: any) => v ? String(v).substring(0, 10) : '';

export default function ActivitatiSection({ dossierId, projectId, onRefresh }: Props) {
  const [activities, setActivities] = useState<DossierActivity[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ ...EMPTY_FORM });
  const [creating, setCreating] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<DossierActivity | null>(null);

  // Activitatea deschisă în modalul de detalii (descriere, responsabil, termene, fișiere, status).
  const [detailId, setDetailId] = useState<number | null>(null);

  const loadActivities = useCallback(async () => {
    try {
      const data = await getDossierActivities(dossierId);
      setActivities(data);
      setLoadError(false);
    } catch (err) {
      console.error(err);
      setLoadError(true);
    }
    finally { setInitialLoading(false); }
  }, [dossierId]);

  useEffect(() => { loadActivities(); }, [loadActivities]);

  const refreshAll = async () => { await loadActivities(); onRefresh?.(); };

  const handleAdd = async () => {
    if (!addForm.name.trim()) return;
    setCreating(true);
    try {
      await createDossierActivity(dossierId, {
        name: addForm.name.trim(),
        description: addForm.description.trim() || undefined,
        status: addForm.status,
        responsible_user_id: addForm.responsible_user_id ?? undefined,
        start_date: addForm.start_date || undefined,
        due_date: addForm.due_date || undefined,
      });
      setShowAddModal(false);
      setAddForm({ ...EMPTY_FORM });
      await refreshAll();
      toast.success('Activitatea a fost adăugată.');
    } catch (err) {
      console.error(err);
      toast.error('Activitatea nu a putut fi adăugată. Încearcă din nou.');
    }
    finally { setCreating(false); }
  };

  const startEdit = (a: DossierActivity) => {
    setEditingId(a.id);
    setEditForm({
      name: a.name,
      description: a.description || '',
      status: a.status || 'pending',
      responsible_user_id: a.responsible_user_id ?? null,
      start_date: toDateStr(a.start_date),
      due_date: toDateStr(a.due_date),
    });
  };

  const handleSaveEdit = async () => {
    if (editingId === null || !editForm.name.trim()) return;
    setSaving(true);
    try {
      await updateDossierActivity(dossierId, editingId, {
        name: editForm.name.trim(),
        description: editForm.description.trim(),
        status: editForm.status,
        responsible_user_id: editForm.responsible_user_id,
        start_date: editForm.start_date || null,
        due_date: editForm.due_date || null,
      });
      setEditingId(null);
      await refreshAll();
      toast.success('Modificările au fost salvate.');
    } catch (err) {
      console.error(err);
      toast.error('Modificările nu au putut fi salvate. Încearcă din nou.');
    }
    finally { setSaving(false); }
  };

  const handleStatusChange = async (id: number, newStatus: ActivityStatus) => {
    try {
      await updateDossierActivity(dossierId, id, { status: newStatus });
      await refreshAll();
      toast.success('Statusul a fost actualizat.');
    } catch (err) {
      console.error(err);
      toast.error('Statusul nu a putut fi actualizat. Încearcă din nou.');
    }
  };

  const handleDelete = async (a: DossierActivity) => {
    try {
      await deleteDossierActivity(dossierId, a.id);
      await refreshAll();
      toast.success('Activitatea a fost ștearsă.');
    } catch (err) {
      console.error(err);
      toast.error('Activitatea nu a putut fi ștearsă. Încearcă din nou.');
    }
  };

  const inputClass = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500";

  // Câmpurile comune formularelor de adăugare/editare.
  const renderFormFields = (form: typeof addForm, setForm: (fn: (f: typeof addForm) => typeof addForm) => void) => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Denumire activitate *</label>
        <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="ex: Obținere aviz de mediu" className={inputClass} />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Descriere</label>
        <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} placeholder="Detalii (opțional)" className={inputClass} />
      </div>
      <UserSelect
        projectId={projectId}
        value={form.responsible_user_id}
        onChange={userId => setForm(f => ({ ...f, responsible_user_id: userId }))}
      />
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
        <SearchableSelect
          options={STATUS_OPTIONS.map(opt => ({ value: opt.value, label: opt.label }))}
          value={form.status}
          onChange={v => setForm(f => ({ ...f, status: v as ActivityStatus }))}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <DateInput label="Dată început" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
        <DateInput label="Termen" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
      </div>
    </div>
  );

  const doneCount = activities.filter(a => a.status === 'completed').length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-600 font-medium">
          {activities.length === 0 ? '0 activități' : `${doneCount}/${activities.length} finalizate`}
        </div>
        <Button variant="primary" size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={() => { setAddForm({ ...EMPTY_FORM }); setShowAddModal(true); }}>
          Adaugă activitate
        </Button>
      </div>

      {/* Add Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setAddForm({ ...EMPTY_FORM }); }}
        title="Activitate nouă"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => { setShowAddModal(false); setAddForm({ ...EMPTY_FORM }); }}>Anulează</Button>
            <Button variant="primary" size="md" onClick={handleAdd} disabled={creating || !addForm.name.trim()} isLoading={creating}>
              {creating ? 'Se creează...' : 'Creează'}
            </Button>
          </div>
        }
      >
        {renderFormFields(addForm, setAddForm)}
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={editingId !== null}
        onClose={() => setEditingId(null)}
        title="Editare activitate"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => setEditingId(null)}>Anulează</Button>
            <Button variant="primary" size="md" onClick={handleSaveEdit} disabled={saving || !editForm.name.trim()} isLoading={saving}>
              {saving ? 'Se salvează...' : 'Salvează'}
            </Button>
          </div>
        }
      >
        {renderFormFields(editForm, setEditForm)}
      </Modal>

      {initialLoading ? (
        <div className="text-center py-8 text-sm text-gray-500">Se încarcă...</div>
      ) : loadError ? (
        <EmptyState
          icon={<AlertTriangle className="w-12 h-12 text-red-400" />}
          title="Activitățile nu au putut fi încărcate"
          description="A apărut o eroare la încărcarea activităților. Verifică conexiunea și încearcă din nou."
          action={{ label: 'Reîncearcă', onClick: () => { setInitialLoading(true); setLoadError(false); loadActivities(); } }}
        />
      ) : activities.length === 0 ? (
        <EmptyState
          icon="file"
          title="Nicio activitate adăugată"
          description="Adaugă activități generice pentru acest dosar (task-uri libere): denumire, responsabil, termen și status."
          action={{ label: 'Adaugă activitate', onClick: () => { setAddForm({ ...EMPTY_FORM }); setShowAddModal(true); } }}
        />
      ) : (
        <div className="overflow-hidden border border-gray-200 rounded-lg">
          <div className="divide-y divide-gray-100">
            {activities.map(a => {
              return (
                <div key={a.id} className="bg-white">
                  <div className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors group">
                    <StatusDot color={STATUS_DOT_COLOR[a.status] || 'gray'} size="sm" />
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm ${a.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-800 font-medium'}`}>{a.name}</div>
                      <div className="flex flex-wrap items-center gap-3 mt-0.5">
                        {a.responsible_name && (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <User className="w-3.5 h-3.5" />{a.responsible_name}
                          </span>
                        )}
                        {a.due_date && (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <CalendarDays className="w-3.5 h-3.5" />{formatDateDisplay(toDateStr(a.due_date))}
                          </span>
                        )}
                        {!!a.doc_count && a.doc_count > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500" title={`${a.doc_count} document(e) atașat(e)`}>
                            <Paperclip className="w-3.5 h-3.5" />{a.doc_count}
                          </span>
                        )}
                        {a.source_wp_id && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-600 border border-blue-100" title={`Migrată din OpenProject #${a.source_wp_id}`}>
                            din OpenProject
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Schimbare status inline: butoane ◀ ▶ + badge */}
                    <div className="flex-shrink-0">
                      <StatusMover
                        value={a.status}
                        onChange={s => handleStatusChange(a.id, s)}
                        size="sm"
                      />
                    </div>

                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => setDetailId(a.id)} className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-100 transition-colors" title="Detalii">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button onClick={() => startEdit(a)} className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-100 transition-colors" title="Editează">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => setDeleteTarget(a)} className="p-1.5 text-gray-400 hover:text-red-500 rounded hover:bg-red-100 transition-colors" title="Șterge">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {a.description && (
                    <div className="px-4 pb-2 pl-9">
                      <div className="text-xs text-gray-500 italic">{a.description}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {detailId !== null && (
        <ActivityDetailModal
          activityId={detailId}
          onClose={() => setDetailId(null)}
          onStatusChange={() => refreshAll()}
        />
      )}

      <ConfirmDelete
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => { if (deleteTarget) handleDelete(deleteTarget); }}
        message={deleteTarget ? `Ești sigur că vrei să ștergi activitatea "${deleteTarget.name}"?` : undefined}
      />
    </div>
  );
}
